import { getUser } from 'auth';
import type { AxiosRequestConfig } from 'axios';
import {
  API_PUBLIC_UPLOAD_TO_S3,
  API_UPLOAD_TO_S3,
} from 'constants/appConstants';
import axios from 'constants/axios';
import type { UUID } from 'interfaces';

export interface IUploadResult {
  uuid: UUID;
  publicKey: UUID;
}

export const extractFileName = (fileURL?: string) => {
  if (!fileURL) return '';

  const regex = /^[^_]*_/;
  const fileURLArray = fileURL.split('/');
  const fileKey = fileURLArray[fileURLArray.length - 1];

  return fileKey.replace(regex, '');
};

export const removeMetadata = (string: string) => {
  const regex = /^.*;base64,/;
  return string.replace(regex, '');
};

export const fixPadding = (string: string) => {
  if (string.length % 4 > 0) {
    return string + '='.repeat(4 - (string.length % 4));
  }

  return string;
};

export const getBase64 = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove metadata at the front
      const encoded = fixPadding(removeMetadata(reader.result as string));

      // Ensure correct padding
      // https://stackoverflow.com/questions/4080988/why-does-base64-encoding-require-padding-if-the-input-length-is-not-divisible-by
      resolve(encoded);
    };
    reader.onerror = (error) => reject(error);
  });

// metadata argument must be an object that will get converted into Tags on S3
export const uploadFileToS3 = async (
  file: File,
  fileType: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  metadata?: unknown,
  config?: AxiosRequestConfig
): Promise<IUploadResult> => {
  const fileAsBase64 = await getBase64(file);

  const response = await axios({
    method: 'POST',
    url: API_UPLOAD_TO_S3(),
    data: {
      fileName: file.name,
      fileType,
      data: fileAsBase64,
    },
    ...config,
  });

  if (!response) {
    throw new Error('Undefined response');
  }

  const responseData = response.data as {
    result?: { uuid: string; publicKey: string };
    error?: { description: string };
  };

  if (response.status === 201) {
    return responseData.result!; // { uuid, publicKey }
  }

  if (response.status === 400) {
    throw new Error(`${responseData.error?.description}`);
  }

  throw new Error(`Error saving file to S3: ${responseData.error}`);
};

export interface IDocUploadResult {
  documentId: string;
  s3FileId: string;
  publicKey: string;
  name: string;
}

export const uploadDocToS3 = async (
  file: File,
  fileType: string,
  assessmentId: string
): Promise<IDocUploadResult> => {
  const path = getUser() ? '/docs/create' : '/public/docs/create';
  const uploaded = await uploadFileToS3(file, fileType, {});
  const response = await axios.post(path, {
    s3FileId: uploaded.uuid,
    assessmentId,
  });
  if (response.status !== 200) {
    throw new Error(
      `Error saving document to S3. ${response.data?.error?.description}`
    );
  }
  return response.data.result;
};

export const uploadCBKDocToS3 = async (
  file: File,
  fileType: string,
  moduleVersionId: number
): Promise<{
  documentId: string;
  s3FileId: string;
  publicKey: string;
  name: string;
}> => {
  const uploaded = await uploadFileToS3(file, fileType, {});
  const response = await axios.post('/docs/cbk/create', {
    s3FileId: uploaded.uuid,
    moduleVersionId,
  });
  if (response.status !== 200) {
    throw new Error(
      `Error saving document to S3: ${response.data?.error?.message}`
    );
  }
  return response.data.result;
};

export const uploadPublicFileToS3 = async (file: File) => {
  const data = new FormData();
  data.append('fileData', file);

  const response = await axios({
    method: 'POST',
    url: API_PUBLIC_UPLOAD_TO_S3(),
    data,
    headers: {
      'Content-Type': 'multipart/form-data;boundary="boundary"',
      'Content-Disposition': `form-data; name="fileData"; filename="${file.name}"`,
    },
  });

  const responseData = response.data as {
    result?: { url: string };
    error?: { description: string };
  };

  if (response.status === 201) {
    return responseData.result;
  }
  throw new Error(`Error saving file to S3: ${responseData.error}`);
};
